<script setup lang="ts">
import {DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import CertificationNotificationFooter from "@/customer-certification/components/CertificationNotificationFooter.vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import CustomerCertificationModalNotEnoughCredit
    from "@/customer-certification/components/certification-modal/CustomerCertificationModalNotEnoughCredit.vue";
import {computed, onMounted, ref} from "vue";
import {Credit} from "@/core/interfaces/Credit";
import {NotificationTemplate as INotificationTemplate} from "@/notification-template/interfaces";
import {useToastStore} from "@/stores/toast.store";
import {customerCertificationApi, organizationCreditApi} from "@/container";
import Customer from "@/customer/interfaces/Customer";
import NotificationTemplate from "@/notification-template/components/NotificationTemplate.vue";
import SMSNotificationTemplateTabs from "@/notification-template/components/sms-tabs/SMSNotificationTemplateTabs.vue";

const {customer, programId, feature} = defineProps<{
    customer: Customer;
    programId?: number;
    surveyId?: number;
    feature?: string;
    slugs?: string[];
}>();

const emits = defineEmits<{
    (e: 'sent'): void;
}>();

const isLoading = ref(false);
const credit = ref<Credit | null>(null);
const notification = ref<INotificationTemplate | null>(null);
const customContent = ref<{ content: string; cost: number } | null>(null);
const {showSuccess} = useToastStore();
const isSending = ref(false);

const hasEnoughCredit = computed(() => {
    return (credit.value?.balance || 0)
        >= (notification.value?.credits || customContent.value?.cost || 1);
});

const title = computed(() => {
    return notification.value ? 'Confirmer l\'envoi du SMS' : 'Choisissez le message à envoyer au patient';
});

const certification = computed(() => {
    return customer.certification || customer.pending_certification;
});

const notificationToSend = computed(() => {
    if (!notification.value && !customContent.value) {
        return null;
    }

    if (notification.value) {
        return notification.value;
    }

    if (customContent.value) {
        return {
            name: "Message personnalisé",
            parsed_content: customContent.value.content,
            type: "sms"
        } as INotificationTemplate;
    }

    return null;
});

const handleModify = () => {
    notification.value = null;
    customContent.value = null;
};

const load = async () => {
    isLoading.value = true;

    try {
        await loadOrganizationBalance();
    } finally {
        isLoading.value = false;
    }
};

const loadOrganizationBalance = async () => {
    return organizationCreditApi()
        .getBalance()
        .then((response) => {
            credit.value = response;
        });
};

const send = async (): Promise<void> => {
    if (!notification.value && !customContent.value?.content) {
        return Promise.resolve();
    }

    const data = notification.value ? {
        notification_template_id: notification.value.id
    } : {
        content: customContent.value!.content
    };

    isSending.value = true;
    return await customerCertificationApi()
        .createNotification(certification.value.id, data)
        .then(() => {
            showSuccess({content: 'Le SMS a été envoyé avec succès.'});
            window.dispatchEvent(new Event('refresh-customer'));
            emits('sent');
        })
        .finally(() => {
            isSending.value = false;
        });
};

onMounted(() => {
    load();
});
</script>

<template>
  <DialogScrollContent
    class="min-w-[80%]" :aria-describedby="undefined"
    @open-auto-focus="(event) => event.preventDefault()"
  >
    <DialogTitle>
      {{ title }}
    </DialogTitle>
    <div v-if="isLoading">
      <BaseSpinner />
    </div>
    <div v-else>
      <div
        v-if="!hasEnoughCredit"
        class="mt-5"
      >
        <CustomerCertificationModalNotEnoughCredit />
      </div>
      <div v-else>
        <div v-if="!customContent && !notification">
          <SMSNotificationTemplateTabs
            :customer-id="customer.id"
            :program-id="programId"
            :survey-id="surveyId"
            :slugs="slugs"
            :feature="feature"
            @template-selected="template => notification = template"
            @custom-content="content => customContent = content"
          />
        </div>
        <div v-else-if="notificationToSend">
          <NotificationTemplate
            :template="notificationToSend"
            selectable
            :button-primary="false"
            @selected="handleModify"
          />
        </div>
      </div>
    </div>
    <certification-notification-footer
      v-if="(notification || customContent) && credit !== null"
      :balance="credit?.balance"
      :customer="customer"
      :cost="notification?.credits ?? customContent?.cost ?? 0"
      :on-send="send"
      :loading="isSending"
    />
  </DialogScrollContent>
</template>

<style scoped>

</style>
